import {
  asPlaybookId,
  checkPlaybookSelected,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { asSignalId } from "@repo/app-types/entities/signal";
import type { SignalMatch } from "@repo/app-types/entities/signalMatch";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconPencil } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";
import {
  useGetSignalQuery,
  useSignalDrilldownQuery,
} from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../../components/performancePlaybookSwitcher.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../../hooks/useModalNavigation.js";
import {
  checkIsVoiceAgentOption,
  usePlaybookAndVoiceAgentOptions,
} from "../../../hooks/usePlaybookAndVoiceAgentOptions.js";
import { buildSignalFilterQueryArgs } from "../../filters/buildFilterQueryArgs.js";
import { useFilters } from "../../filters/filters.js";
import { usePlaybookFilterDefaults } from "../../filters/usePlaybookFilterDefaults.js";
import { SignalMatchRateChart } from "./signalMatchRateChart.js";
import { SignalMatchSlideoverProvider } from "./signalMatchSlideoverContainer.js";
import { SignalMetadataBreakdown } from "./signalMetadataBreakdown.js";
import { SignalOutcomeBreakdown } from "./signalOutcomeBreakdown.js";
import { SignalRecentMatches } from "./signalRecentMatches.js";
import { SignalSummary } from "./signalSummary.js";

export const SignalDrilldownPage: FC = () => {
  const { signalId: signalIdParam, playbookId: playbookIdParam } = useParams();
  if (!signalIdParam) {
    throw new Error("Missing signalId param");
  }
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param");
  }

  const signalId = asSignalId(signalIdParam);
  const playbookId = asPlaybookId(playbookIdParam);

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [
    {
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    },
    renderFilters,
  ] = useFilters(
    [
      "teammateGroupIds",
      "dateRange",
      "dateBasis",
      "outcome",
      "customFields",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "playbookConditions",
    ],
    { playbookId, signalId, view: "team" },
    playbookFilterDefaults,
  );

  const now = useNow();

  const { scope, filters } = useMemo(
    () =>
      buildSignalFilterQueryArgs(now, {
        playbookId,
        dateRange,
        dateBasis,
        teammateGroupIds: teammateGroupIds ?? null,
        outcome,
        customFields,
        signalId,
        signalMetadataFields,
        totalRecordingDuration,
        recordingCount,
        playbookConditions,
      }),
    [
      now,
      playbookId,
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    ],
  );

  const {
    data: drilldownData,
    isLoading,
    isFetching,
  } = useSignalDrilldownQuery({
    scope,
    filters,
  });

  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery();
  const selectedPlaybook = useMemo(
    () => minimalPlaybooks?.find((p) => p.id === playbookId),
    [minimalPlaybooks, playbookId],
  );

  // Restrict the playbook switcher to the signal's scope; unrestricted while
  // the signal is still loading.
  const { data: signal } = useGetSignalQuery(signalId);
  const { options: playbookAndVoiceAgentOptions } =
    usePlaybookAndVoiceAgentOptions();
  const scopedPlaybookIds = useMemo(() => {
    const selection = signal?.playbook_scope;
    if (selection === undefined) {
      return undefined;
    }
    return playbookAndVoiceAgentOptions
      .filter((option) =>
        checkPlaybookSelected(
          selection,
          option.value,
          checkIsVoiceAgentOption(option),
        ),
      )
      .map((option) => option.value);
  }, [signal?.playbook_scope, playbookAndVoiceAgentOptions]);
  const outcomeDefinition = useMemo(
    () => getOutcomeDefinitionFromPlaybook(selectedPlaybook),
    [selectedPlaybook],
  );

  const modalNavigation = useModalNavigation();

  const handleMatchClick = useCallback(
    (match: SignalMatch) => {
      modalNavigation.push(`matches/${encodeURIComponent(match.matchId)}`);
    },
    [modalNavigation],
  );

  const dateRangeDays = Math.round(dateRange.length.as("days"));
  const dateRangeEnd = dateRange.end ?? DateTime.utc();
  const dateRangeStart = dateRangeEnd.minus(dateRange.length);

  return (
    <PageLayout>
      <PageHeader
        title={drilldownData?.current.signalName}
        isLoading={isLoading}
        actions={
          <Link to={appPaths.settingsSignal.build({ signalId })}>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              icon={<IconPencil className="size-4" />}
            >
              Edit signal
            </Button>
          </Link>
        }
        filterBar={renderFilters({
          isLoading: isFetching,
          leadingItems: (
            <PerformancePlaybookSwitcher
              staysOnFullPath
              shouldIncludeVoiceAgents
              selectablePlaybookIds={scopedPlaybookIds}
            />
          ),
        })}
      />

      <PageBody>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader style={LoaderStyle.ZOOMIES} />
          </div>
        ) : null}

        {drilldownData ? (
          <div className="flex flex-col gap-6 lg:flex-row">
            <div className="min-w-0 space-y-6 lg:flex-1">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
                <div className="lg:w-64 lg:shrink-0">
                  <SignalSummary
                    stats={drilldownData.current}
                    priorStats={drilldownData.prior}
                    dateRangeDays={dateRangeDays}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <SignalMatchRateChart
                    buckets={drilldownData.current.buckets}
                    startDate={dateRangeStart}
                    endDate={dateRangeEnd}
                    isRelativeRange={dateRange.end === undefined}
                  />
                </div>
              </div>

              <SignalOutcomeBreakdown
                outcomeBreakdown={drilldownData.current.outcomeBreakdown}
                playbooks={minimalPlaybooks}
                playbookId={playbookId}
                filterLabels={signalMetadataFields.map((c) => c.label)}
              />

              <SignalMetadataBreakdown
                metadataBreakdown={drilldownData.current.metadataBreakdown}
                outcomeDef={outcomeDefinition}
              />
            </div>

            <SignalRecentMatches
              matches={drilldownData.current.recentMatches}
              outcomeDef={outcomeDefinition}
              onMatchClick={handleMatchClick}
            />
          </div>
        ) : null}

        <SignalMatchSlideoverProvider
          value={{
            matches: drilldownData?.current.recentMatches ?? [],
            metadataFields:
              drilldownData?.current.metadataBreakdown.map((f) => ({
                fieldId: f.fieldId,
                fieldKey: f.fieldKey,
              })) ?? [],
            signalName: drilldownData?.current.signalName ?? "",
          }}
        >
          <NavigationSlideoverContainer />
        </SignalMatchSlideoverProvider>
      </PageBody>
    </PageLayout>
  );
};
